import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './InvoiceList.css';
//import InvoiceForm from '../InvoiceFormx';
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Pagination, PaginationItem, PaginationLink, Popover, PopoverBody, Row, Spinner } from 'reactstrap';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaEdit, FaEllipsisH, FaFileInvoice, FaFilePdf, FaFilter, FaInfo, FaPlus, FaSearch, FaTrash, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { DELIVERY_NOTE_PAGE } from '../../routes/routeConstants';
import PaymentBloc from './PaymentBloc';
import { format, parse } from 'date-fns';
import { invoiceSupplierActions } from '../../sagas/invoiceSupplierSlice';
import InvoiceForm from './InvoiceForm';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { generateSupplierInvoicePdf } from '../../helpers/generateSupplierInvoicePdf';
import Select from "react-select";
import ViewFacture from './ViewFacture';
import { supplierActions } from '../../sagas/supplierSlice';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FaFileExcel } from 'react-icons/fa';
import { companyActions } from '../../sagas/companySlice';

const SupplierInvoiceList = () => {
    const dispatch = useDispatch();
    const { invoices, loading } = useSelector((state) => state.invoiceSupplier);
    const { company } = useSelector(state => state.auth);
    const { companies } = useSelector((state) => state.companies);
    const defaultCompanyId = useMemo(
        () => companies?.find((item) => item.isDefault)?.id ?? null,
        [companies]
    );
    const { t } = useTranslation();
    const searchInputRef = useRef(null);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monthStartDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const tomorrowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const pad = (n) => n.toString().padStart(2, '0');
    const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const getMonthDateRange = (monthValue) => {
        if (!monthValue) {
            return { dateFrom: '', dateTo: '' };
        }

        const [year, month] = monthValue.split('-').map(Number);
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);

        return {
            dateFrom: formatDate(firstDay),
            dateTo: formatDate(lastDay),
        };
    };
    const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
    const [filters, setFilters] = useState({
        companyId: null,
        fournisseurId: null,
        dateFrom: formatDate(monthStartDate),
        dateTo: formatDate(tomorrowDate),
        query: '',
        month: ''
    });

    useEffect(() => {
        if (suppliers.length === 0) {
            // Fetch suppliers for supplier report
            const fetchSuppliers = async () => {
                dispatch(supplierActions.search({
                    query: ""
                }));
            };

            fetchSuppliers();
        }
        dispatch(companyActions.getAll({}));
    }, []);

    useEffect(() => {
        if (defaultCompanyId && filters.companyId == null) {
            setFilters((prev) => ({ ...prev, companyId: defaultCompanyId }));
        }
    }, [defaultCompanyId]);

    useEffect(() => {
        // Auto focus on mount
        searchInputRef.current?.focus();

        // Add keyboard shortcut
        const handleKeyDown = (e) => {
            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [invoices]);

    useEffect(() => {
        let payload = {
            ...filters
        };
        dispatch(invoiceSupplierActions.search(payload));
    }, [filters.companyId, filters.fournisseurId, filters.dateFrom, filters.dateTo]);


    const debouncedSearch = () => {
        dispatch(invoiceSupplierActions.search({
            ...filters,
        }));
    }

    const fournisseurOptions = suppliers?.map(sup => ({
        solde: sup.solde,
        totalDebit: sup.totalDebit,
        totalCredit: sup.totalCredit,
        value: sup.id,
        label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom} (${sup.code})` : `${sup.entreprise}(${sup.code})`,
    })) ?? [];

    const companyOptions = companies?.map(item => ({
        value: item.id,
        label: item.name,
    })) ?? [];

    const companyOptionsWithAll = [
        { value: null, label: t('common.all', 'Tous') },
        ...companyOptions
    ];

    const getInvoiceCompanyName = (invoice) => {
        if (invoice?.companyName) return invoice.companyName;
        if (invoice?.company?.name) return invoice.company.name;
        return companies?.find(item => item.id === invoice?.companyId)?.name || '-';
    };

    const getInvoiceCompany = (invoice) => {
        if (invoice?.company?.name) {
            return invoice.company;
        }

        return companies?.find(item => item.id === invoice?.companyId) || company;
    };

    const handleSupplierChange = (value) => {
        setFilters(f => ({ ...f, fournisseurId: value }));
    };

    const clearFilters = () => {
        setFilters({
            companyId: defaultCompanyId,
            query: '',
            fournisseurId: null,
            dateFrom: formatDate(monthStartDate),
            dateTo: formatDate(tomorrowDate),
            month: ''
        });
    };

    const handleEditInvoice = (invoice) => {
        setSelectedInvoiceForEdit(invoice);
        setShowEditModal(true);

        let payload = {
            ...filters
        };
        dispatch(invoiceSupplierActions.search(payload));
    };

    const handleAnnulerFacture = (facture) => {
        Swal.fire({
            title: t('facture.deleteConfirmTitle'),
            text: t('facture.deleteConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const payload = {
                        id: facture.id,
                        motifAnnulation: "",
                        onSuccess: () => {
                            Swal.fire(t('facture.deleted'), '', 'success');
                            debouncedSearch();
                        },
                        onError: () => {
                            Swal.fire({
                                icon: 'error',
                                title: t('delivery.deleteError'),
                                text: t('delivery.deleteErrorMessage')
                            });
                        }
                    };

                    dispatch(invoiceSupplierActions.cancelInvoiceRequest(payload));
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: t('common.error'),
                        text: t('delivery.deleteError')
                    });
                }
            }
        });
    };


    const getStatusBadge = (status) => {
        switch (status) {
            case 'IMPAYE':
                return <Badge color="danger">Impayée</Badge>;
            case 'PAYEE':
                return <Badge color="success">Payée</Badge>;
            case 'PARTIELLEMENT_PAYEE':
                return <Badge color="warning">Partiellement payée</Badge>;
            case 'ANNULEE':
                return <Badge color="secondary">Annulée</Badge>;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999,
                }}
            >
                <Spinner color="primary" />
            </div>
        );
    }

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedInvoiceForEdit(null);

        let payload = {
            ...filters
        };
        dispatch(invoiceSupplierActions.search(payload));

    };

    const handleExportInvoicesExcel = () => {
        if (!invoices || invoices.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning') || 'Attention',
                text: t('facture.noInvoices') || 'Aucune facture à exporter'
            });
            return;
        }

        const data = invoices.map(inv => ({
            "N° Facture": inv.numero || '',
            "Fournisseur": inv.fournisseurNom || '',
            "Entreprise": getInvoiceCompanyName(inv),
            "Date facture": inv.dateFacture || '',
            "Statut": inv.statut || '',
            "N° Fact Fourn.": inv.numeroFactfournisseur || '',
            "Total HT": inv.montantHT || 0,
            "TVA": inv.montantTVA || 0,
            "Total TTC": inv.montantTTC || 0,
            "Montant réglé": inv.montantRegle || 0,
            "BL associés": inv.referenceBons || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(data);
        const colWidths = Object.keys(data[0]).map(key => ({
            wch: Math.max(
                key.length,
                ...data.map(row => String(row[key] ?? '').length)
            )
        }));
        worksheet['!cols'] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'FacturesFournisseurs');

        const excelBuffer = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const filename = `Factures_Fournisseurs_${filters.dateFrom || ''}_${filters.dateTo || ''}.xlsx`;
        saveAs(blob, filename);
    };

    const handleGenerateInvoicePdf = (invoice) => {
        Swal.fire({
            title: t('common.loading'),
            text: t('facture.pdfLoading', 'Chargement des reglements...'),
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        dispatch(invoiceSupplierActions.fetchInvoiceReglements({
            numero: invoice.numero,
            onSuccess: (reglements = []) => {
                Swal.close();
                generateSupplierInvoicePdf(invoice, {
                    company: getInvoiceCompany(invoice),
                    openInNewTab: true,
                    reglements,
                });
            },
            onError: () => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: t('facture.pdfError', 'Erreur lors de la generation du PDF.'),
                });
            }
        }));
    };

    const handleDownloadListPdf = () => {
        try {
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 14;

            const title = t('nav.supplierReports') || 'Liste des factures fournisseurs';
            const period =
                `${t('common.from') || 'Du'} ${filters.dateFrom || '-'} ` +
                `${t('common.to') || 'au'} ${filters.dateTo || '-'}`;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(16);
            doc.text(title, margin, 18);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.text(period, margin, 26);

            if (company?.name) {
                const right = pageWidth - margin;
                doc.setFontSize(9);
                doc.text(company.name, right, 12, { align: 'right' });
                if (company.address) doc.text(company.address, right, 16, { align: 'right' });
                if (company.email || company.phone1) {
                    doc.text(
                        `${company.email ? company.email : ''}${company.email && company.phone1 ? ' | ' : ''}${company.phone1 || ''}`,
                        right, 20, { align: 'right' }
                    );
                }
            }

            const tableBody = (invoices || []).map(inv => ([
                inv.numero || '',
                inv.fournisseurNom || '',
                getInvoiceCompanyName(inv),
                inv.dateFacture || '',
                inv.statut || '',
                inv.numeroFactfournisseur || '',
                formatPrice(inv.montantTTC || 0, false),
                formatPrice(inv.montantHT || 0, false),
                formatPrice(inv.montantTVA || 0, false),
            ]));

            autoTable(doc, {
                startY: 32,
                head: [[
                    t('facture.table.invoiceNb') || 'N° Facture',
                    t('purchase.supplier') || 'Fournisseur',
                    t('company.company', 'Entreprise'),
                    t('facture.table.Date') || 'Date',
                    t('facture.table.status') || 'Statut',
                    'N° Fact Fourn.',
                    t('facture.table.totalTtc') || 'Total TTC',
                    t('facture.table.totalHt') || 'Total HT',
                    t('reception.totalTva') || 'TVA'
                ]],
                body: tableBody,
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: {
                    fillColor: [52, 73, 94],
                    textColor: 255,
                    fontStyle: 'bold',
                    valign: 'middle',
                },
                columnStyles: {
                    0: { cellWidth: 35, halign: 'center', headerHalign: 'center' },
                    1: { cellWidth: 48, halign: 'left', headerHalign: 'left' },
                    2: { cellWidth: 42, halign: 'left', headerHalign: 'left' },
                    3: { cellWidth: 25, halign: 'center', headerHalign: 'center' },
                    4: { cellWidth: 28, halign: 'center', headerHalign: 'center' },
                    5: { cellWidth: 34, halign: 'center', headerHalign: 'center' },
                    6: { cellWidth: 24, halign: 'right', headerHalign: 'right' },
                    7: { cellWidth: 24, halign: 'right', headerHalign: 'right' },
                    8: { cellWidth: 18, halign: 'right', headerHalign: 'right' },
                },
                alternateRowStyles: { fillColor: [245, 245, 245] },
                margin: { left: 12, right: 12 },
                didDrawPage: () => {
                    const pageHeight = doc.internal.pageSize.getHeight();
                    const str = `${t('common.page') || 'Page'} ${doc.internal.getNumberOfPages()}`;
                    doc.setFontSize(8);
                    doc.text(str, pageWidth / 2, pageHeight - 6, { align: 'center' });
                },
            });

            const filename = `Factures_Fournisseurs_${filters.dateFrom || ''}_${filters.dateTo || ''}.pdf`;
            const blob = doc.output('blob');
            const url = URL.createObjectURL(blob);
            const win = window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 1200);
            if (!win) doc.save(filename);
        } catch (e) {
            console.error('PDF export error', e);
            Swal.fire({
                icon: 'error',
                title: t('common.error') || 'Erreur',
                text: t('facture.pdfError') || 'Erreur lors de la génération du PDF.',
            });
        }
    };

    return (
        <Container fluid>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center gap-2">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaFileInvoice className="text-primary me-2" />
                                {t('facture.list')}
                            </h4>
                            <div className="d-flex gap-2">
                                <div style={{ minWidth: '260px' }}>
                                    <Select
                                        id="companyIdHeader"
                                        options={companyOptionsWithAll}
                                        value={companyOptionsWithAll.find(option => option.value === filters.companyId) || null}
                                        onChange={(selectedOption) => setFilters(f => ({ ...f, companyId: selectedOption ? selectedOption.value : null }))}
                                        placeholder={t('company.selectCompany', 'Filtrer par entreprise')}
                                        classNamePrefix="react-select"
                                    />
                                </div>
                                <Button
                                    color="primary"
                                    onClick={handleDownloadListPdf}
                                    size="sm"
                                    className="d-flex align-items-center"
                                >
                                    <FaFilePdf className="me-2" size={14} />
                                    {t('facture.downloadListPdf') || 'Export PDF'}
                                </Button>
                                <Button
                                    color="success"
                                    onClick={handleExportInvoicesExcel}
                                    size="sm"
                                    className="d-flex align-items-center"
                                    title={t('facture.exportExcel') || 'Exporter Excel'}
                                >
                                    <FaFileExcel className="me-2" size={14} />
                                    Excel
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {/* Filtres et actions en lot */}
            <Card className="shadow-sm mb-4">
                <CardBody>
                    <Row className="mb-3 g-2 align-items-end">
                        <Col lg={3} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label className="mb-1">{t('reglement.search')}</Label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <FaSearch />
                                    </span>
                                    <Input
                                        type="text"
                                        innerRef={searchInputRef}
                                        placeholder={t('facture.searchPlaceholder')}
                                        value={filters.query}
                                        onChange={e => {
                                            const value = e.target.value.toUpperCase();
                                            setFilters(prevFilters => ({
                                                ...prevFilters,
                                                query: value
                                            }));
                                        }}
                                        className="text-uppercase"
                                        style={{ textTransform: 'uppercase' }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') {
                                                debouncedSearch(e.target.value.toUpperCase());
                                            }
                                        }}
                                    />
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={2}>
                            <FormGroup>
                                <Label for="supplierSelect" className="form-label">
                                    <FaUser className="me-1" /> Fournisseur
                                </Label>
                                <Select
                                    id="supplierSelect"
                                    options={fournisseurOptions}
                                    isLoading={suppliersLoading}
                                    value={fournisseurOptions.find(option => option.value === filters.fournisseurId)}
                                    onChange={(option) => handleSupplierChange(option?.value)}
                                    placeholder="Sélectionner..."
                                    isClearable
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 })
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="month" className="mb-1">{t('common.month', 'Mois')}</Label>
                                <Input
                                    type="month"
                                    id="month"
                                    value={filters.month}
                                    onChange={e => {
                                        const month = e.target.value;
                                        const { dateFrom, dateTo } = getMonthDateRange(month);
                                        setFilters(f => ({
                                            ...f,
                                            month,
                                            dateFrom: dateFrom || f.dateFrom,
                                            dateTo: dateTo || f.dateTo,
                                        }));
                                    }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateFrom" className="mb-1">Date début</Label>
                                <Input
                                    type="date"
                                    id="dateFrom"
                                    value={filters.dateFrom}
                                    onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={3} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="dateTo" className="mb-1">Date fin</Label>
                                <Input
                                    type="date"
                                    id="dateTo"
                                    value={filters.dateTo}
                                    onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value, month: '' }))}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={1} md={1} sm={6} xs={12} className="d-flex align-items-end">
                            <Button
                                color="secondary"
                                onClick={clearFilters}
                                title={t('common.clearFilters')}
                                className="mb-3"
                                style={{ minHeight: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <FaFilter />
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner color="primary" />
                    <div className="mt-2 text-muted">{t('common.loading')}</div>
                </div>
            ) : (
                <>
                    <table className="table table-hover table-striped table-bordered" style={{ fontSize: 12 }}>
                        <thead className="thead-light">
                            <tr>
                                <th className="align-middle text-center">{t('facture.table.invoiceNb')}</th>
                                <th className="align-middle text-center">{t('purchase.supplier')}</th>
                                <th className="align-middle text-center">{t('company.company', 'Entreprise')}</th>
                                <th className="align-middle text-center">{t('facture.dateFacture')}</th>
                                <th className="align-middle text-center">{t('common.user')}</th>
                                <th className="align-middle text-center">N° Fact Fourn.</th>
                                <th className="align-middle text-center">{t('facture.table.totalTtc')}</th>
                                <th className="align-middle text-center">{t('facture.table.totalHt')}</th>
                                <th className="align-middle text-center">{t('reception.totalTva')}</th>
                                <th className="align-middle text-center">{t('facture.montantRegle')}</th>
                                <th className="align-middle text-center">{t('facture.table.receiptNumbers')}</th>
                                <th className="align-middle text-center">{t('facture.table.status')}</th>
                                <th className="align-middle text-center">{t('facture.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {invoices?.length === 0 && (
                                <tr>
                                    <td colSpan="13" className="text-center text-muted">
                                        {t('facture.noInvoices')}
                                    </td>
                                </tr>
                            )}
                            {invoices?.map((invoice) => (
                                <tr key={invoice.id}>
                                    <td className="align-middle text-center">{invoice.numero}</td>
                                    <td className="align-middle text-center">{invoice?.fournisseurNom}</td>
                                    <td className="align-middle text-center">{getInvoiceCompanyName(invoice)}</td>
                                    <td className="align-middle text-center">{invoice.dateFacture}</td>
                                    <td className="align-middle text-center">{invoice.createdBy}</td>
                                    <td className="align-middle text-center">{invoice.numeroFactfournisseur || 'N/A'}</td>
                                    <td className="align-middle text-center">{formatPrice(invoice.montantTTC, false)}</td>
                                    <td className="align-middle text-center">{formatPrice(invoice.montantHT, false)}</td>
                                    <td className="align-middle text-center">{formatPrice(invoice.montantTVA, false)}</td>
                                    <td className="align-middle text-center">{formatPrice(invoice.montantRegle, false)}</td>
                                    <td className="align-middle text-center">
                                        {invoice.referenceBons ? (
                                            <div className="d-flex flex-column align-items-center">
                                                {invoice.referenceBons.split(',').map((receipt, index) => (
                                                    <Badge key={index} color="success" className='mb-1' style={{ fontSize: '10px' }}>
                                                        {receipt.trim()}
                                                    </Badge>
                                                ))}
                                            </div>
                                        ) : (
                                            <span className="text-muted">Aucun</span>
                                        )}
                                    </td>
                                    <td className="align-middle text-center">
                                        {getStatusBadge(invoice.statut)}
                                    </td>
                                    <td className="align-middle text-center">
                                        <Button color="secondary" title='Téléchargé PDF' onClick={() => handleGenerateInvoicePdf(invoice)} size="sm" className="me-1 px-2 py-1">
                                            <FaFilePdf size={12} className="mr-2" />
                                        </Button>
                                        <Button
                                            color="warning"
                                            title={t('facture.edit')}
                                            onClick={() => handleEditInvoice(invoice)}
                                            size="sm"
                                            className="me-1"
                                        //disabled={!canEditInvoice(invoice)}
                                        >
                                            <FaEdit size={12} />
                                        </Button>
                                        {invoice.statut !== 'ANNULEE' && (
                                            <Button
                                                color="danger"
                                                title={t('commande.cancel')}
                                                onClick={() => handleAnnulerFacture(invoice)}
                                                className="me-1"
                                                size="sm"
                                            >
                                                <FaTrash size={12} />
                                            </Button>
                                        )}

                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-light fw-bold">
                                <td colSpan="6" className="text-end">{t('common.totals')}</td>
                                <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantTTC, 0), false)}</td>
                                <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantHT, 0), false)}</td>
                                <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantTVA, 0), false)}</td>
                                <td className="align-middle text-center">{formatPrice(invoices.reduce((acc, invoice) => acc + invoice.montantRegle, 0), false)}</td>
                                <td colSpan="3"></td>
                            </tr>
                        </tfoot>
                    </table>
                </>)}
            <Modal
                isOpen={showEditModal}
                toggle={handleCloseEditModal}
                size="xl"
                className="modal-fullscreen-lg-down"
                style={{ maxWidth: '95%', width: '95%', margin: '10px auto' }}
            >
                <ModalHeader toggle={handleCloseEditModal}>
                    {t('facture.editInvoice')} - {selectedInvoiceForEdit?.numero}
                </ModalHeader>
                <ModalBody>
                    {selectedInvoiceForEdit && (
                        <ViewFacture
                            invoice={selectedInvoiceForEdit}
                            onSave={handleCloseEditModal}
                            onCancel={handleCloseEditModal}
                            isEditing={true}
                        />
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default SupplierInvoiceList;

